import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';
import './ReportTheme.css';
import './WatchReportPage.css';
import TokenBar from './TokenBar';
import ReportBoxWhisker from './ReportBoxWhisker';
import ReportRunChart from './ReportRunChart';
import ReportThumb from './ReportThumb';
import { usePageTracking } from './hooks/useAnalytics';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const fmtRate = (v, d = 1) => (v == null ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(d)}`);
const fmtDate = iso => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const ordinal = n => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

function HealthRing({ score }) {
  const R = 56, C = 2 * Math.PI * R, frac = Math.max(0, Math.min(100, score)) / 100;
  // Note: the svg must NOT carry the flex .rpt-ring class itself — as a
  // block-level element with no auto margins it left-drifts. It lives in
  // the hero flex row now and sizes itself.
  return (
    <svg width="76" height="76" viewBox="0 0 130 130" className="rpt-heroring">
      <circle cx="65" cy="65" r={R} fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="9" />
      <circle cx="65" cy="65" r={R} fill="none" stroke="var(--rpt-brass)" strokeWidth="9" strokeLinecap="round"
        strokeDasharray={`${(C * frac).toFixed(1)} ${C.toFixed(1)}`} transform="rotate(-90 65 65)" />
      <text x="65" y="62" textAnchor="middle" fontSize="36" fontWeight="600" fill="currentColor" fontFamily="Georgia, serif">{score}</text>
      <text x="65" y="84" textAnchor="middle" fontSize="11" letterSpacing="1.5" fill="currentColor" opacity="0.5">HEALTH</text>
    </svg>
  );
}

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const vals = points.map(p => p.r), lo = Math.min(...vals), hi = Math.max(...vals), span = Math.max(hi - lo, 0.5);
  const W = 320, H = 46;
  const pts = points.map((p, i) => `${(i / (points.length - 1) * W).toFixed(1)},${(H - ((p.r - lo) / span) * (H - 8) - 4).toFixed(1)}`).join(' ');
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ marginTop: 8 }}>
      <polyline points={pts} fill="none" stroke="var(--rpt-brass)" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

export default function WatchReportPage({ demo = false }) {
  const { userId, watchId, token } = useParams();
  const [state, setState] = useState({ status: 'loading', report: null });
  const [chartMode, setChartMode] = useState('drift');
  const [expandedRun, setExpandedRun] = useState(null);
  const collectionPath = demo ? '/report-example' : token ? `/r/${token}` : `/analytics/${userId}`;
  usePageTracking(demo ? 'example_watch_report' : 'watch_report', { page_title: 'ChronoLog Watch Report' });

  useEffect(() => {
    let cancelled = false;
    if (demo) {
      import('./exampleReport').then(m => {
        if (cancelled) return;
        const r = m.EXAMPLE_WATCH_REPORTS[watchId];
        setState(r ? { status: 'ready', report: r } : { status: 'notfound', report: null });
      });
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const rpc = token ? 'get_watch_report_by_token' : 'get_watch_report';
        const body = token ? { p_token: token, p_watch_id: watchId } : { p_user_id: userId, p_watch_id: watchId };
        const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${rpc}`, {
          method: 'POST',
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const report = await res.json();
        if (!cancelled) setState(report ? { status: 'ready', report } : { status: 'notfound', report: null });
      } catch { if (!cancelled) setState({ status: 'error', report: null }); }
    })();
    return () => { cancelled = true; };
  }, [userId, watchId, token, demo]);

  const { report } = state;
  const sparkPts = useMemo(() => {
    if (!report) return [];
    return (report.runs || []).filter(r => r.rate_spd != null)
      .map(r => ({ t: new Date(r.last_at).getTime(), r: Number(r.rate_spd) })).sort((a, b) => a.t - b.t);
  }, [report]);

  // Whether the RPC shipped per-measurement points: changes both the chart
  // (drift segments vs aggregate sparkline) and what the header must claim.
  const hasPoints = useMemo(
    () => (report?.runs || []).some(r => Array.isArray(r.points) && r.points.length >= 2),
    [report]
  );

  if (state.status === 'loading')
    return <div className="rpt-page"><main className="rpt-main"><div className="rpt-status">Loading…</div></main></div>;

  if (state.status === 'notfound' || state.status === 'error') {
    return (
      <div className="rpt-page">
        <Helmet><title>Watch Report — ChronoLog</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <main className="rpt-main"><div className="rpt-status">
          <h1 className="rpt-serif">Report unavailable</h1>
          <p><Link to={collectionPath} className="rpt-back">‹ Collection</Link></p>
        </div></main>
      </div>
    );
  }

  const { watch, summary, benchmark, health, audio } = report;
  const title = [watch.brand, watch.model].filter(Boolean).join(' ') || 'Watch';
  const modelName = watch.model || 'watches';
  const sub = [watch.caliber && `CAL. ${watch.caliber}`, watch.movement_bph && `${watch.movement_bph} BPH`,
    summary && summary.runs_with_rate && `${summary.runs_with_rate} RATED RUNS`,
    report.since_service_at && `SINCE SERVICE ${fmtDate(report.since_service_at)}`].filter(Boolean).join(' · ');

  return (
    <div className="rpt-page">
      <Helmet>
        <title>{title} — ChronoLog Watch Report</title>
        {!demo && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>
      <main className="rpt-main rpt-main--watch">
        {token && report._token && <TokenBar meta={report._token} />}
        {/* A watch-scoped token can't open the collection — hide the back-link
            so it doesn't dead-end. Collection-wide tokens keep it. */}
        {!(token && report._token && report._token.watch_id) && (
          <Link to={collectionPath} className="rpt-back">‹ Collection</Link>
        )}
        <div className="rpt-brandline">CHRONOLOG</div>
        {/* Compact hero: photo · identity+rate · health dial in one row */}
        <div className="rpt-whero">
          <ReportThumb token={token} watchId={watchId} big photoUrl={watch.photo_url} />
          <div className="rpt-whero-main">
            <h1 className="rpt-serif rpt-wtitle">{title}</h1>
            {sub && <div className="rpt-wsub">{sub}</div>}
            {summary && summary.runs_with_rate > 0 && (
              <div className="rpt-wstat">
                <b>{fmtRate(summary.weighted_mean_rate_spd)}</b> s/day avg
                {summary.rate_stddev != null && <> · ±{summary.rate_stddev} between runs</>}
              </div>
            )}
          </div>
          {health && <HealthRing score={health.score} />}
        </div>

        {summary && summary.runs_with_rate > 0 ? (
          <>
            {/* Two views of the same measurements, mirroring the app's
                graphs: drift (cumulative seconds per run; slope = rate) and
                rate (pairwise seconds/day between consecutive measurements). */}
            <div className="rpt-sec rpt-sec--row">
              <span>{hasPoints ? 'TIMEKEEPING · BY TIMING RUN' : 'RATE · SECONDS PER DAY'}</span>
              {hasPoints && (
                <span className="rpt-seg" role="tablist" aria-label="Chart mode">
                  {['drift', 'rate'].map(m => (
                    <button key={m} type="button" role="tab" aria-selected={chartMode === m}
                      className={chartMode === m ? 'on' : ''} onClick={() => setChartMode(m)}>
                      {m === 'drift' ? 'Drift' : 'Rate'}
                    </button>
                  ))}
                </span>
              )}
            </div>
            <div className="rpt-card rpt-card--l">
              <ReportRunChart runs={report.runs} audio={audio} mode={chartMode}
                fallback={<Sparkline points={sparkPts} />} />
              {hasPoints && (
                <div className="rpt-chartnote">
                  <span>{chartMode === 'drift'
                    ? 'each line is one timing run · steeper slope = faster rate'
                    : <>● tap/photo (pairwise s/day) {audio && audio.length > 0 && <>· ○ acoustic timegrapher </>}· one line per run</>}</span>
                </div>
              )}
            </div>

            {benchmark && benchmark.box && (
              <>
                <div className="rpt-sec">AMONG {benchmark.cohort_label.toUpperCase()} <span className="rpt-sec-sub">· {benchmark.bucket} timepieces</span></div>
                <div className="rpt-card rpt-card--l">
                  <div style={{ color: 'var(--rpt-ink)' }}><ReportBoxWhisker box={benchmark.box} you={benchmark.this_rate} height={74} big /></div>
                  <div className="rpt-you">
                    {/* Lead with what we actually rate: accuracy (closeness
                        to zero) and steadiness. The signed-rate percentile
                        read as "80th percentile for daily rate" — which
                        means faster-than, not better-than. */}
                    {benchmark.accuracy_percentile != null ? (
                      <>Your {modelName} keeps time <b>more accurately than {benchmark.accuracy_percentile}%</b>
                        {benchmark.consistency_percentile != null && <> — and runs <b>steadier than {benchmark.consistency_percentile}%</b></>}
                        {' '}of the {benchmark.bucket} {benchmark.cohort_label} timepieces on ChronoLog.</>
                    ) : (
                      <>Your {modelName} sits at the <b>{benchmark.rate_percentile != null ? ordinal(benchmark.rate_percentile) : '—'} percentile</b> for daily rate
                        {benchmark.consistency_percentile != null && <> — steadier than <b>{benchmark.consistency_percentile}%</b></>} of the {benchmark.bucket} {benchmark.cohort_label} timepieces on ChronoLog.</>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Consistency now lives on the hero stat line — no separate card. */}
            <details className="rpt-exp">
              <summary>Detailed timing data</summary>
              <div className="rpt-table-wrap">
                <table className="rpt-table">
                  <thead><tr><th>Started</th><th>Meas.</th><th>Span</th><th>Rate</th></tr></thead>
                  <tbody>
                    {[...report.runs].sort((a, b) => new Date(b.started_at) - new Date(a.started_at)).map(r => {
                      const expandable = Array.isArray(r.points) && r.points.length >= 2;
                      const open = expandedRun === r.id;
                      return (
                        <React.Fragment key={r.id}>
                          <tr className={expandable ? 'rpt-row-x' : undefined}
                            onClick={expandable ? () => setExpandedRun(open ? null : r.id) : undefined}>
                            <td>{expandable && <span className="rpt-rowchev">{open ? '▾' : '▸'} </span>}{fmtDate(r.started_at)}</td>
                            <td>{r.n}</td>
                            <td>{r.span_days != null ? `${r.span_days}d` : '—'}</td>
                            <td>{r.rate_spd != null ? `${fmtRate(r.rate_spd, 2)}` : 'too short'}</td>
                          </tr>
                          {open && (
                            <tr className="rpt-subrow"><td colSpan="4">
                              <table className="rpt-table rpt-table--sub">
                                <thead><tr><th>Measured</th><th>Drift</th><th>Rate since prev.</th></tr></thead>
                                <tbody>
                                  {r.points.map((p, j) => {
                                    const prev = j > 0 ? r.points[j - 1] : null;
                                    const days = prev ? (new Date(p.t) - new Date(prev.t)) / 86400000 : 0;
                                    const pr = prev && days > 0.01 ? (Number(p.off) - Number(prev.off)) / days : null;
                                    return (
                                      <tr key={j}>
                                        <td>{new Date(p.t).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                                        <td>{fmtRate(p.off, 2)}s</td>
                                        <td>{pr != null ? `${fmtRate(pr, 1)} s/d` : '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {audio && audio.length > 0 && (
                <div className="rpt-table-wrap" style={{ marginTop: '0.8rem' }}>
                  <div className="rpt-sec" style={{ textAlign: 'left' }}>ACOUSTIC (TIMEGRAPHER)</div>
                  <table className="rpt-table">
                    {/* Rate only: the phone-mic pipeline does not measure beat
                        error or amplitude reliably, so the report never shows
                        them (the app dropped its amplitude tile for the same
                        reason). */}
                    <thead><tr><th>Date</th><th>Rate (s/day)</th></tr></thead>
                    <tbody>
                      {audio.map((a, i) => (
                        <tr key={i}>
                          <td>{fmtDate(a.at)}</td><td>{fmtRate(a.rate_spd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </details>
          </>
        ) : (
          <div className="rpt-card rpt-card--l" style={{ marginTop: '1rem' }}>
            <div className="rpt-you">Keep timing this watch to unlock its report — a health score needs at least 6 measurements spanning a week.</div>
          </div>
        )}

        <footer className="rpt-footer">Measured with <a href="/">ChronoLog</a>. Reflects this watch’s last sync ({fmtDate(report.last_synced_at)}).</footer>
      </main>
    </div>
  );
}
